'use client'

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { type birdiePastRoundsCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import { useViewportScale, transformPresets } from 'src/hooks/useViewportScale'
import { BirdieFairnessBody } from './BirdieFairnessBody'
import { BirdieRoundCard } from './BirdieRoundCard'
import { BirdieBallDefs } from './GolfBall'
import { verifierLink, type BirdiePastRound } from './pastRounds'
import type { RoundCardView } from './roundCardModel'
import type { Translate } from './tileViewModel'

type Copy = ReturnType<typeof birdiePastRoundsCopy>

type Props = Readonly<{
  round: BirdiePastRound
  /** The same card the sidebar draws, at full density (`roundCardModel`). */
  view: RoundCardView
  copy: Copy
  /**
   * The host's LANGUAGE-PINNED `t` (`BirdiePastRounds`), not a hook call of its
   * own: `useClientSideTranslation` hands out a `t` whose identity never
   * changes and which reads whatever language the i18n instance is on, so a
   * second consumer can render the previous language after a runtime switch.
   * One `t`, pinned once, for the rail and everything it opens.
   */
  t: Translate
  onClose: () => void
}>

const TITLE_ID = 'birdie-round-sheet-title'

/**
 * The Birdie round modal (B19 section 3) — one past round, told properly.
 *
 * It is the round CARD at full density, plus the provably-fair body under a
 * divider. The card is the same component the history sidebar draws compact,
 * so the round a player clicked and the round they read are the same drawing
 * at two sizes; the fairness body is `<BirdieFairnessBody>` (B33), the ONE
 * Birdie fairness rendering, which the shared `<CrashRoundModal>` mounts too
 * for a round opened from the bet history — three entry points, one block.
 *
 * B17's four-block sheet (its own golfer card with the capsule's stat bars, a
 * separate score row, a "what paid" list) is gone with the redesign — but its
 * two load-bearing halves are kept verbatim: `readBirdieFairness`, which is the
 * only guarded reader of the engine's reveal, and `verifierLink`, which is the
 * shared URL contract the crash modal and operator-api's `verifyURL` also
 * speak.
 *
 * Portalled to `<body>`. It cannot live inside the board's overlay or
 * `.right-floating-panel`: both are transformed, and a transformed ancestor
 * becomes the containing block for `position: fixed`, which would trap the
 * backdrop inside the rail. Portalling also puts it outside every scaled
 * container, so it consumes the widget's own viewport scale the way the crash
 * sheet does.
 *
 * SECURITY / SPOILERS: only settled, revealed rounds ever reach this component
 * — `birdiePastRounds` withholds a round until its reveal hold has elapsed, so
 * a modal can never show a result the clip has not reached.
 *
 * Accessibility: `role="dialog"` + `aria-modal`, named by a heading built from
 * the round's own content, Escape and backdrop close, a minimal Tab trap, and
 * focus restored to the disc that opened it.
 */
export function BirdieRoundSheet({ round, view, copy, t, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null)

  const scaleStyle = useViewportScale({
    transform: transformPresets.basic,
    transformOrigin: 'center center'
  })

  // Focus management: remember the opener, focus the dialog, restore on unmount.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    return () => {
      opener?.focus()
    }
  }, [])

  // Escape closes; Tab cycles inside the dialog (the crash sheet's minimal trap,
  // `CrashRoundModal.tsx:295-317`).
  //
  // Bound as a NATIVE listener on the dialog element rather than as a JSX
  // `onKeyDown`: `role="dialog"` is a non-interactive role, so a key handler in
  // the markup is a `jsx-a11y/no-noninteractive-element-interactions` warning
  // for a dialog that is behaving exactly as a dialog should. Same element,
  // same bubbling phase, same behaviour — one fewer suppression.
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], [tabindex]:not([tabindex="-1"])'
      )
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (first === undefined || last === undefined) return
      // The dialog CONTAINER counts as "before the first focusable" going
      // backwards: on open the focus is put on it (`tabIndex={-1}`), and a
      // Shift+Tab from there matched neither end, so the browser walked out of
      // the portal to whatever sits behind the modal — and since this listener
      // is bound to the dialog, neither Tab nor Escape was handled after that.
      // Forward needs no arm: Tab from the container lands on the close cross
      // by DOM order.
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    dialog.addEventListener('keydown', onKeyDown)
    return () => {
      dialog.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  if (typeof document === 'undefined') return null

  const sheet = (
    // B19b — the modal now opens from INSIDE the shared history sidebar, which
    // closes itself on any `mousedown` that lands outside its own DOM. This
    // sheet is portalled to <body>, so every click in it is "outside" that
    // panel: without this the first tap on the reveal closed the sidebar and
    // unmounted the sheet mid-read. `RightSidebar` guards the crash modal by
    // name (`RightSidebar.tsx:76-98`); stopping the event here keeps the fix in
    // Birdie's own file and covers every other outside-click listener at once
    // (the results rail has one too). The sheet's own backdrop-to-close is a
    // real button and is unaffected — it fires on `click`, on a child.
    // Not an interaction: the handler only stops an ancestor's outside-click
    // listener from seeing a click that already landed inside this dialog.
    // Every real control in here is a button, and Escape closes from anywhere.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- see above
    <div
      className="birdie-sheet"
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
    >
      {/* The backdrop is a REAL button rather than a click handler on the
          layout div: same tap-outside-to-close behaviour, no click-versus-
          keyboard mismatch, and no need to stop propagation on the dialog. It
          is out of the tab order on purpose — it duplicates the × beside the
          title, which is inside the trap, and Escape closes from anywhere. */}
      <button
        aria-label={copy.close}
        className="birdie-sheet__backdrop"
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />
      {/* The entrance keyframe on `__dialog` owns its own transform, so the
          viewport scale lives on a separate, un-animated element or the two
          fight (the crash sheet learned this the same way). */}
      {/*
        The sheet is portalled to `document.body`, OUTSIDE the board's overlay,
        so it cannot borrow the gradients the board declares — its balls would
        draw unpainted. Declaring them again is free and safe: the ids are fixed
        and the definitions identical (`GolfBall.tsx`).
      */}
      <BirdieBallDefs />

      <div className="birdie-sheet__scale" style={scaleStyle}>
        <div
          aria-labelledby={TITLE_ID}
          aria-modal="true"
          className="birdie-sheet__dialog"
          ref={dialogRef}
          role="dialog"
          tabIndex={-1}
        >
          {/* The dialog's name, built from the round's own content rather than
              written twice: the card's eyebrow shows the same id and its head
              the same golfer. Visually hidden because the card already says it.
              A dot join, not a sentence — nothing here needs translating.
              `roundLabel` already carries the word "Round" (the model builds it
              once for both surfaces), so it is not prefixed again here. */}
          <h2 className="birdie-sr-only" id={TITLE_ID}>
            {[view.roundLabel, view.golfer.name].join(' · ')}
          </h2>
          {/* OUTSIDE the scroller below, and still inside the dialog: the
              dialog used to be the scroll container itself, so on a viewport
              short enough to make the card scroll (the reveal block is tall)
              the only visible dismiss control scrolled away with it. Escape
              and the backdrop always worked; the cross now stays put too, and
              stays inside the focus trap and the dialog's own subtree. */}
          <button
            aria-label={copy.close}
            className="birdie-sheet__close"
            onClick={onClose}
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>

          <div className="birdie-sheet__scroll">
            <BirdieRoundCard density="full" view={view}>
              <BirdieFairnessBody
                fairness={round.fairness}
                t={t}
                verifierHref={verifierLink(round)}
              />
            </BirdieRoundCard>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(sheet, document.body)
}
